/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type KeyboardEvent as ReactKeyboardEvent,
	type MouseEvent as ReactMouseEvent,
} from 'react';
import { cssMap, jsx } from '@compiled/react';

import GrabArea from './grab-area';
import { bindAll, type UnbindFn } from 'bind-event-listener';
import rafSchd from 'raf-schd';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	COLLAPSED_ORG_SELECTOR_WIDTH,
	DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH,
	MAX_ORG_SELECTOR_EXPANDED_WIDTH,
	MIN_ORG_SELECTOR_EXPAND_WIDTH,
	ORG_SELECTOR_WIDTH_LS_KEY,
} from '../../../constants';
import {
	NAVIGATION_CHANNEL,
	SWITCHER_ORG_NAME_EXPAND_PANEL,
	UI_EVENT_TYPE,
} from '../../../common/utils/analytics';

type ResizeControlProps = {
	onDragStart?: () => void;
	onDragEnd?: () => void;
	updateOrgSelectorComponentWidth: (width: number) => void;
};

enum AnalyticsActionType {
	CLICKED = 'clicked',
	DRAGGED = 'dragged',
}

enum AnalyticsSubjectIdType {
	MOUSE = 'mouse',
	KEYBOARD = 'keyboard',
}

const resizeControlStyles = cssMap({
	root: {
		insetBlockEnd: 0,
		insetBlockStart: 0,
		insetInlineStart: '100%',
		outline: 'none',
	},
});

const ResizeControl = ({
	updateOrgSelectorComponentWidth,
	onDragStart,
	onDragEnd,
}: ResizeControlProps) => {
	// Distance of mouse from left sidebar onMouseDown
	const offset = useRef(0);
	const keyboardEventTimeout = useRef<number>();
	const unbindEvents = useRef<UnbindFn | null>(null);
	const [width, setWidth] = useState(COLLAPSED_ORG_SELECTOR_WIDTH);
	const [lastWidth, setLastWidth] = useState(width);
	const [isResizing, setIsResizing] = useState(false);
	const [isGrabAreaFocused, setIsGrabAreaFocused] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const widthRef = useRef(width);

	const stableWidth = useRef(width);

	const styleRef = useRef<HTMLStyleElement | null>(null);

	useEffect(() => {
		stableWidth.current = width;
	}, [width]);

	const isCollapsed = width === COLLAPSED_ORG_SELECTOR_WIDTH;

	const updateOrgSelectorWidth = useCallback(
		(newWidth: number) => {
			updateOrgSelectorComponentWidth(newWidth);
			setWidth(newWidth);
		},
		[updateOrgSelectorComponentWidth],
	);

	// Update state from cache on mount
	// Get initial width value from local storage, if available, else fallback to default (COLLAPSED_ORG_SELECTOR_WIDTH)
	useEffect(() => {
		const widthFromStorage = localStorage.getItem(ORG_SELECTOR_WIDTH_LS_KEY);

		let cachedWidth = widthFromStorage ? parseInt(widthFromStorage) : COLLAPSED_ORG_SELECTOR_WIDTH;

		updateOrgSelectorWidth(cachedWidth);
	}, [updateOrgSelectorWidth]);

	// Every time other than mount, update local storage
	const notFirstRun = useRef(false);
	useEffect(() => {
		if (notFirstRun.current) {
			localStorage.setItem(ORG_SELECTOR_WIDTH_LS_KEY, `${width}`);
		}

		if (!notFirstRun.current) {
			notFirstRun.current = true;
		}
	}, [width]);

	const fireEvent = useCallback(
		(action: AnalyticsActionType, actionSubjectId: AnalyticsSubjectIdType, newWidth: number) => {
			if (createAnalyticsEvent) {
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					actionSubject: SWITCHER_ORG_NAME_EXPAND_PANEL,
					actionSubjectId: actionSubjectId,
					action: action,
					attributes: {
						newWidth: newWidth,
					},
				}).fire(NAVIGATION_CHANNEL);
			}
		},
		[createAnalyticsEvent],
	);

	const fireClickEvent = useCallback(
		(actionSubjectId: AnalyticsSubjectIdType) => {
			fireEvent(
				AnalyticsActionType.CLICKED,
				actionSubjectId,
				isCollapsed
					? Math.max(lastWidth, DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH)
					: COLLAPSED_ORG_SELECTOR_WIDTH,
			);
		},
		[fireEvent, isCollapsed, lastWidth],
	);

	const fireDragEvent = useCallback(
		(actionSubjectId: AnalyticsSubjectIdType, newWidth: number) => {
			fireEvent(AnalyticsActionType.DRAGGED, actionSubjectId, newWidth);
		},
		[fireEvent],
	);

	const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		if (!isGrabAreaFocused || isResizing) {
			return false;
		}

		const { key } = event;

		const isLeftOrTopArrow =
			key === 'ArrowLeft' || key === 'ArrowUp' || key === 'Left' || key === 'Up';
		const isRightOrBottomArrow =
			key === 'ArrowRight' || key === 'ArrowDown' || key === 'Right' || key === 'Down';

		const isSpaceOrEnter = key === 'Enter' || key === 'Spacebar' || key === ' ';
		if (isSpaceOrEnter) {
			fireClickEvent(AnalyticsSubjectIdType.KEYBOARD);
			toggleOrgSelectorCb();
			event.preventDefault();
		}

		if (isLeftOrTopArrow || isRightOrBottomArrow) {
			event.preventDefault(); // prevent content scroll

			const step = 10;
			const stepValue = isLeftOrTopArrow ? -step : step;
			const hasModifierKey = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
			let width = stableWidth.current + stepValue;

			if (isRightOrBottomArrow) {
				if (width <= DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH) {
					// snap open i.e., expand to default width
					width = DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH;
				} else if (width > MAX_ORG_SELECTOR_EXPANDED_WIDTH || hasModifierKey) {
					// expand to max width
					width = MAX_ORG_SELECTOR_EXPANDED_WIDTH;
				}
			} else {
				if (width < DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH) {
					// snap closed i.e., collapse
					width = COLLAPSED_ORG_SELECTOR_WIDTH;
				} else if (hasModifierKey) {
					// snap to default expanded width
					width = DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH;
				}
			}

			fireDragEvent(AnalyticsSubjectIdType.KEYBOARD, width);

			// Nesting the setTimeout within requestAnimationFrame helps
			// the browser schedule the setTimeout call in an efficient manner
			requestAnimationFrame(() => {
				keyboardEventTimeout.current = window.setTimeout(() => {
					keyboardEventTimeout.current && clearTimeout(keyboardEventTimeout.current);

					setLastWidth(width);
					setIsResizing(false);
					updateOrgSelectorWidth(width);
				}, 50);
			});
		}
	};

	const [onUpdateResize] = useState(() =>
		rafSchd(({ clientX }: { clientX: number }) => {
			if (clientX < 0) {
				onResizeOffLeftOfScreen();
				return;
			}

			const delta = clientX - offset.current;

			if (delta === 0) {
				return;
			}

			const width = stableWidth.current;

			const newWidth = Math.max(
				Math.min(width + delta, MAX_ORG_SELECTOR_EXPANDED_WIDTH),
				COLLAPSED_ORG_SELECTOR_WIDTH,
			);

			widthRef.current = newWidth;

			updateOrgSelectorComponentWidth(newWidth);
		}),
	);

	const onResizeOffLeftOfScreen = () => {
		onUpdateResize.cancel();
		unbindEvents.current?.();
		unbindEvents.current = null;
		offset.current = 0;

		onDragEnd?.();

		fireDragEvent(AnalyticsSubjectIdType.MOUSE, COLLAPSED_ORG_SELECTOR_WIDTH);
		collapseOrgSelector();
	};

	const expandOrgSelector = useCallback(() => {
		let expandWidth = Math.max(lastWidth, DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH);

		setLastWidth(lastWidth);
		updateOrgSelectorWidth(expandWidth);
		setIsResizing(false);
	}, [lastWidth, updateOrgSelectorWidth]);

	const collapseOrgSelector = useCallback(() => {
		setLastWidth(width);
		updateOrgSelectorWidth(COLLAPSED_ORG_SELECTOR_WIDTH);
		setIsResizing(false);
	}, [width, updateOrgSelectorWidth]);

	const toggleOrgSelector = useCallback(() => {
		if (isCollapsed) {
			expandOrgSelector();
		} else {
			collapseOrgSelector();
		}
	}, [isCollapsed, expandOrgSelector, collapseOrgSelector]);

	const toggleOrgSelectorCb = useCallback(
		(event?: ReactMouseEvent | ReactKeyboardEvent<HTMLButtonElement>) => {
			// don't cascade down
			event?.stopPropagation();

			toggleOrgSelector();
		},
		[toggleOrgSelector],
	);

	const onFinishResizing = () => {
		onDragEnd?.();

		if (widthRef.current === 0) {
			// click
			fireClickEvent(AnalyticsSubjectIdType.MOUSE);
			toggleOrgSelector();
		} else if (widthRef.current < MIN_ORG_SELECTOR_EXPAND_WIDTH) {
			// snap closed i.e., collapse
			fireDragEvent(AnalyticsSubjectIdType.MOUSE, COLLAPSED_ORG_SELECTOR_WIDTH);
			collapseOrgSelector();
		} else if (
			widthRef.current >= MIN_ORG_SELECTOR_EXPAND_WIDTH &&
			widthRef.current < DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH
		) {
			// snap open i.e., expand to default width
			fireDragEvent(AnalyticsSubjectIdType.MOUSE, DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH);
			setLastWidth(DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH);
			setIsResizing(false);
			updateOrgSelectorWidth(DEFAULT_ORG_SELECTOR_EXPANDED_WIDTH);
		} else {
			// resize to desired width
			fireDragEvent(AnalyticsSubjectIdType.MOUSE, widthRef.current);
			setLastWidth(widthRef.current);
			setIsResizing(false);
			updateOrgSelectorWidth(widthRef.current);
		}

		unbindEvents.current?.();
		unbindEvents.current = null;
		onUpdateResize.cancel();
		widthRef.current = 0;
		offset.current = 0;
	};

	const onMouseDown = (event: ReactMouseEvent<HTMLButtonElement>) => {
		// Only allow left (primary) clicks to trigger resize
		if (event.button !== 0) {
			return;
		}

		// It is possible for a mousedown to fire during a resize
		// Example: the user presses another pointer button while dragging
		if (isResizing) {
			// the resize will be cancelled by our global event listeners
			return;
		}

		offset.current = event.clientX;
		widthRef.current = 0;

		unbindEvents.current = bindAll(window, [
			{
				type: 'mousemove',
				listener: (event: MouseEvent) => {
					onUpdateResize({ clientX: event.clientX });
				},
			},
			{
				type: 'mouseup',
				listener: onFinishResizing,
			},
			{
				type: 'mousedown',
				listener: (() => {
					let hasFramePassed = false;
					requestAnimationFrame(() => {
						hasFramePassed = true;
					});

					return function listener() {
						if (hasFramePassed) {
							onFinishResizing();
						}
					};
				})(),
			},
			{
				type: 'visibilitychange',
				listener: onFinishResizing,
			},
			{
				type: 'click',
				listener: onFinishResizing,
			},
			{
				type: 'keydown',
				listener: (event: KeyboardEvent) => {
					// Can cancel resizing by pressing "Escape"
					// Will return org selector panel to the same size it was before the resizing started
					if (event.key === 'Escape') {
						widthRef.current = stableWidth.current;

						onFinishResizing();
					}
				},
			},
		]);

		onDragStart?.();

		setIsResizing(true);
	};

	const onFocus = useCallback(() => {
		setIsGrabAreaFocused(true);
	}, []);

	const onBlur = useCallback(() => {
		setIsGrabAreaFocused(false);
	}, []);

	const isMaxWidth = width === MAX_ORG_SELECTOR_EXPANDED_WIDTH;

	const getOrgSelectorPercentage = (currentWidth: number) => {
		const total =
			(currentWidth - COLLAPSED_ORG_SELECTOR_WIDTH) /
			(MAX_ORG_SELECTOR_EXPANDED_WIDTH - COLLAPSED_ORG_SELECTOR_WIDTH);

		if (total < 0) {
			return 0;
		}
		if (total > 1) {
			return 100;
		}

		return Math.floor(total * 100);
	};

	const orgSelectorPercentageExpanded = getOrgSelectorPercentage(width);

	useEffect(() => {
		if (isResizing) {
			const cursor = isCollapsed ? 'e-resize' : isMaxWidth ? 'w-resize' : 'ew-resize';
			const styles = `
			/*
			 * Blocking selection while resizing
			 * Notes:
			 * - This prevents a user selection being caused by resizing
			 * - Safari + Firefox → all good
			 * - Chrome → This will undo the current selection while resizing (not ideal)
			 *   - The current selection will resume after resizing
			 *
			 * Setting the cursor to be ${cursor} on all elements so that even if the user
			 * pointer slips off the resize handle, the cursor will still be the resize cursor
			 *
			 * Disabling pointer events on iframes when resizing,
			 * as iframes will swallow user events when the user is over them
			 *
			 * eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			 */
			* {
			  user-select: none !important;
			  cursor: ${cursor} !important;
			}
			iframe {
			  pointer-events: none !important;
			}
		  `;

			const style = document.createElement('style');
			style.innerHTML = styles;
			document.head.appendChild(style);
			styleRef.current = style;

			return () => {
				if (styleRef.current) {
					document.head.removeChild(styleRef.current);
					styleRef.current = null;
				}
			};
		}
	}, [isResizing, isCollapsed, isMaxWidth]);

	return (
		<div css={resizeControlStyles.root}>
			<GrabArea
				isCollapsed={isCollapsed}
				isMaxWidth={isMaxWidth}
				onKeyDown={onKeyDown}
				onMouseDown={onMouseDown}
				onFocus={onFocus}
				onBlur={onBlur}
				orgSelectorPercentageExpanded={orgSelectorPercentageExpanded}
				isResizing={isResizing}
			/>
		</div>
	);
};

export default ResizeControl;
